/**
 * my-page「看板」タブ デザイン view のテーブル一式。
 * 開発 view（MyProjectsTable）と同一の PL/主担当 抽出結果を、デザイン向け列で表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business my-page 看板タブ デザイン view の PL/主担当 担当案件テーブル（予定/実績/完了予定日/designer 列）
 * @graph-connects none
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { indexTasksByChild, useMyEvaluationTasks } from '../../../hooks/useEvaluationSurvey.js';
import { EvaluationSurveyCell } from './EvaluationSurveyCell.js';
import { tokyoToday, visibleDesignChildren } from './design-row-visibility.js';
import { filterIntegratedProducts, filterProjects } from './project-search.js';
import { orphanSurveyGroups, type SurveyOnlyGroup } from './survey-rows.js';
import type { EvaluationTask } from '../../../types/idt-design-evaluation.js';
import { SlackChannelButton } from '../project/SlackChannelButton.js';
import { BACKLOG_BASE, getStatusVariant } from '../project/ChildTicketsModal.js';
import type {
  DesignChildTicket,
  PmoOverdueProject,
  MyProjectsBoardIntegratedProduct,
} from '../../../types/project.js';

/** デザインpt表示（小数は 1 桁まで丸め、null は '-'）。 @graph-connects none */
function formatDesignPt(value: number | null): string {
  if (value == null) return '-';
  return (Math.round(value * 10) / 10).toString();
}

interface MyDesignProjectsTableProps {
  integratedProducts: MyProjectsBoardIntegratedProduct[];
  standaloneProjects: PmoOverdueProject[];
  /** 案件キー / 案件名の検索語。空なら絞り込まない（未回答アンケートの行は検索中も常に出す） */
  query?: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイン view の担当案件テーブル。統合プロダクト配下のチャンクを折り畳み、スタンドアロンを通常行で表示
 * @graph-connects MyDesignProjectRow [calls] 通常行描画
 * @graph-connects MyDesignProjectsIntegratedRow [calls] 統合プロダクト折り畳み行描画
 * @graph-connects visibleDesignChildren [calls] 決着済み子票行の除外
 */
export function MyDesignProjectsTable({
  integratedProducts,
  standaloneProjects,
  query = '',
}: MyDesignProjectsTableProps) {
  const { t } = useTranslation();
  // アンケートタスクは担当案件とは別口径（完了済み子チケットが対象）で取得する。
  // 担当案件テーブルは未完了案件しか出さないため、案件一覧の行データからは導出できない。
  const { data: surveyData } = useMyEvaluationTasks();
  const tasks = useMemo(() => surveyData?.tasks ?? [], [surveyData]);
  const taskByChild = indexTasksByChild(tasks);
  // デザイン view ではデザイン子チケットがあり、かつ未完了（進行中）の案件のみ表示する。
  // dev 専用案件やデザインが全完了した案件はノイズになるため除外する。
  const hasActiveDesign = (p: PmoOverdueProject): boolean =>
    p.designChildCount > 0 && p.designIncompleteCount > 0;
  // 決着済み（完了/処理済 + 回答済み or 対象外）から 1 週間を超えた子票行は落とす。
  // 行が 1 本も残らなかった母票はヘッダだけになるので母票ごと落とす。ただし元から
  // 子票が 0 件の母票（集計 CTE と子票クエリで対象種別が違うケース）は従来どおり残す。
  // タスクが未取得（読み込み中 / 取得失敗）の間は「task なし = 対象外」と区別できず
  // 未回答を落としてしまうため、絞り込まずに全子票を出す。
  const today = tokyoToday();
  const tasksLoaded = surveyData !== undefined;
  const withVisibleChildren = (list: PmoOverdueProject[]): PmoOverdueProject[] =>
    list.filter(hasActiveDesign).flatMap((p) => {
      if (!tasksLoaded) return [p];
      const designChildren = visibleDesignChildren(p.designChildren, taskByChild, today);
      if (designChildren.length === 0 && p.designChildren.length > 0) return [];
      return [{ ...p, designChildren }];
    });
  const designStandalone = withVisibleChildren(standaloneProjects);
  const designIntegrated = integratedProducts
    .map((ip) => ({
      ...ip,
      chunkProjects: withVisibleChildren(ip.chunkProjects),
    }))
    .filter((ip) => ip.chunkProjects.length > 0);

  // **未回答は必ずどこかの行に出す**。上の絞り込み（未完了デザイン子あり）は
  // 「デザインが全部終わった案件」を落とすが、アンケートは完了した子票にしか出ないので、
  // 落ちた案件こそ未回答が溜まりやすい。行が無い未回答をここで拾って末尾に足す。
  // 回答済みも回答日から 1 週間は拾う（回答直後に行ごと消えないようにする）。
  const renderedChildKeys = new Set(
    [...designStandalone, ...designIntegrated.flatMap((ip) => ip.chunkProjects)].flatMap((p) =>
      p.designChildren.map((c) => c.issueKey),
    ),
  );
  const orphanGroups = orphanSurveyGroups(tasks, renderedChildKeys, today);

  // 検索は案件の行だけに効かせる。未回答アンケートの行（orphan）は絞り込まずに出す。
  const shownIntegrated = filterIntegratedProducts(query, designIntegrated);
  const shownStandalone = filterProjects(query, designStandalone);
  const searching = query.trim() !== '';

  if (designStandalone.length === 0 && designIntegrated.length === 0 && orphanGroups.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('myProjectsBoard.emptyDesign')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.issueKey')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.summary')}</th>
            <th className="py-2 px-3 font-medium text-right">
              {t('myProjectsBoard.columns.planned')}
            </th>
            <th className="py-2 px-3 font-medium text-right">
              {t('myProjectsBoard.columns.actual')}
            </th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.designDueDate')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.designer')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.status')}</th>
            <th className="py-2 px-3 font-medium text-center">
              {t('myProjectsBoard.columns.survey')}
            </th>
            <th className="py-2 px-3 font-medium text-right">
              {t('myProjectsBoard.columns.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {shownIntegrated.map(({ ip, matchedChunks }) => (
            <MyDesignProjectsIntegratedRow
              key={ip.integratedProductKey}
              ip={ip}
              taskByChild={taskByChild}
              forceExpanded={matchedChunks}
            />
          ))}
          {shownStandalone.map((p) => (
            <MyDesignProjectRow key={p.issueKey} project={p} taskByChild={taskByChild} />
          ))}
          {orphanGroups.map((group) => (
            <SurveyOnlyGroup key={group.parentIssueKey} group={group} />
          ))}
        </tbody>
      </table>
      {searching &&
        shownIntegrated.length === 0 &&
        shownStandalone.length === 0 &&
        orphanGroups.length === 0 && (
          <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            {t('myProjectsBoard.searchNoMatch')}
          </p>
        )}
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイン view の統合プロダクト折り畳み行。クリックで配下チャンクを展開
 * @graph-connects MyDesignProjectRow [calls] 配下チャンク行描画
 */
function MyDesignProjectsIntegratedRow({
  ip,
  taskByChild,
  forceExpanded,
}: {
  ip: MyProjectsBoardIntegratedProduct;
  taskByChild: Map<string, EvaluationTask>;
  /** 検索で配下チャンクが一致したときは自動で展開する */
  forceExpanded: boolean;
}) {
  const [toggled, setToggled] = useState(false);
  const expanded = toggled || forceExpanded;

  return (
    <>
      <tr
        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
        onClick={() => {
          setToggled(!toggled);
        }}
      >
        <td className="py-2 px-3 text-[var(--color-accent-primary)] whitespace-nowrap">
          <span className="mr-1">{expanded ? '▼' : '▶'}</span>
          <Link
            to="/projects/$pjKey"
            params={{ pjKey: ip.integratedProductKey }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="hover:underline"
          >
            {ip.integratedProductKey}
          </Link>
        </td>
        <td
          className="py-2 px-3 max-w-md truncate font-medium text-[var(--color-text-primary)]"
          title={ip.summary}
          colSpan={8}
        >
          {ip.summary}
        </td>
      </tr>
      {expanded &&
        ip.chunkProjects.map((p) => (
          <MyDesignProjectRow key={p.issueKey} project={p} taskByChild={taskByChild} indent />
        ))}
    </>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイン view の母票ヘッダ行 + 配下子票行。母票固有の値（見積・完了予定日・Slack）はヘッダに 1 回だけ置く
 * @graph-connects SlackChannelButton [calls] Slack channelボタン描画
 * @graph-connects MyDesignChildRow [calls] 子票行描画
 */
function MyDesignProjectRow({
  project,
  taskByChild,
  indent = false,
}: {
  project: PmoOverdueProject;
  taskByChild: Map<string, EvaluationTask>;
  indent?: boolean;
}) {
  return (
    <>
      {/*
        母票ヘッダ。designer / ステータス / アンケートは**子票ごとに異なる**ので空にする。
        ここに母票集計の designers（カンマ区切り）を出すと、下の子票行の designer と
        食い違って「行とアンケートがずれている」ように見える。
      */}
      <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <td
          className={`py-2 px-3 whitespace-nowrap text-[var(--color-accent-primary)] ${
            indent ? 'pl-10' : ''
          }`}
        >
          <Link
            to="/projects/$pjKey"
            params={{ pjKey: project.issueKey }}
            className="hover:underline"
          >
            {project.issueKey}
          </Link>
        </td>
        <td
          className="max-w-md truncate py-2 px-3 font-medium text-[var(--color-text-primary)]"
          title={project.summary}
        >
          {project.summary}
        </td>
        <td className="py-2 px-3 text-right whitespace-nowrap">
          {formatDesignPt(project.designPlanned)}
        </td>
        <td className="py-2 px-3 text-right whitespace-nowrap">
          {formatDesignPt(project.designActual)}
        </td>
        <td className="py-2 px-3 whitespace-nowrap">{project.designDueDate ?? '-'}</td>
        <td className="py-2 px-3" colSpan={3} />
        <td className="py-2 px-3 text-right">
          <SlackChannelButton
            pjChannelId={project.pjSlackChannelId}
            devChannelId={project.devSlackChannelId}
          />
        </td>
      </tr>
      {project.designChildren.map((child) => (
        <MyDesignChildRow
          key={child.issueKey}
          child={child}
          task={taskByChild.get(child.issueKey) ?? null}
          indent={indent}
        />
      ))}
    </>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイン view の子票行。1 行 = デザイン / 情報設計 子チケット 1 件 = アンケート最大 1 通
 * @graph-connects EvaluationSurveyCell [calls] アンケート列セル描画
 */
function MyDesignChildRow({
  child,
  task,
  indent,
}: {
  child: DesignChildTicket;
  task: EvaluationTask | null;
  indent: boolean;
}) {
  return (
    <tr className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-hover)]">
      <td
        className={`py-2 px-3 whitespace-nowrap text-[var(--color-accent-primary)] ${
          indent ? 'pl-16' : 'pl-8'
        }`}
      >
        <a
          href={`${BACKLOG_BASE}${child.issueKey}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          {child.issueKey}
        </a>
      </td>
      <td className="max-w-md truncate py-2 px-3" title={child.summary}>
        {child.summary}
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">{formatDesignPt(child.planned)}</td>
      <td className="py-2 px-3 text-right whitespace-nowrap">{formatDesignPt(child.actual)}</td>
      {/* 完了予定日は母票の custom field なので子票行には無い（ヘッダ側に 1 回だけ出す）。 */}
      <td className="py-2 px-3" />
      <td className="max-w-[160px] truncate py-2 px-3" title={child.designer ?? ''}>
        {child.designer ?? '-'}
      </td>
      <td className="py-2 px-3">
        <Badge variant={getStatusVariant(child.status)} size="sm" solid>
          {child.status}
        </Badge>
      </td>
      <td className="py-2 px-3 text-center whitespace-nowrap">
        <EvaluationSurveyCell task={task} />
      </td>
      <td className="py-2 px-3" />
    </tr>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件一覧から落ちた母票の未回答アンケート行。母票見出し + 子票行で、通常の行と同じ形で出す
 * @graph-connects EvaluationSurveyCell [calls] アンケート列セル描画
 */
function SurveyOnlyGroup({ group }: { group: SurveyOnlyGroup }) {
  return (
    <>
      <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <td className="py-2 px-3 whitespace-nowrap text-[var(--color-accent-primary)]">
          <a
            href={`${BACKLOG_BASE}${group.parentIssueKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {group.parentIssueKey}
          </a>
        </td>
        <td
          className="max-w-md truncate py-2 px-3 font-medium text-[var(--color-text-primary)]"
          title={group.parentSummary}
        >
          {group.parentSummary}
        </td>
        {/*
          デザインが全部完了して一覧から落ちた母票。予定pt / 完了予定日 / Slack は
          案件一覧の行データが無いので出せない（アンケートを出すのが目的の行）。
          未回答が 1 件でも残っていれば「未回答」。全部答え終わっていれば「回答済」で、
          この行は回答から 1 週間で消える。
        */}
        <td className="py-2 px-3 text-xs text-[var(--color-text-muted)]" colSpan={7}>
          {group.tasks.some((task) => task.state === 'pending')
            ? 'デザイン完了（アンケート未回答）'
            : 'デザイン完了（アンケート回答済）'}
        </td>
      </tr>
      {group.tasks.map((task) => (
        <tr
          key={task.childIssueKey}
          className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-hover)]"
        >
          <td className="py-2 px-3 pl-8 whitespace-nowrap text-[var(--color-accent-primary)]">
            <a
              href={`${BACKLOG_BASE}${task.childIssueKey}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {task.childIssueKey}
            </a>
          </td>
          <td className="max-w-md truncate py-2 px-3" title={task.childSummary}>
            {task.childSummary}
          </td>
          <td className="py-2 px-3 text-right whitespace-nowrap">-</td>
          <td className="py-2 px-3 text-right whitespace-nowrap">{formatDesignPt(task.pt)}</td>
          <td className="py-2 px-3 whitespace-nowrap">{task.completedDate}</td>
          <td className="max-w-[160px] truncate py-2 px-3" title={task.designer}>
            {task.designer}
          </td>
          <td className="py-2 px-3">
            <Badge variant={getStatusVariant('完了')} size="sm" solid>
              完了
            </Badge>
          </td>
          <td className="py-2 px-3 text-center whitespace-nowrap">
            <EvaluationSurveyCell task={task} />
          </td>
          <td className="py-2 px-3" />
        </tr>
      ))}
    </>
  );
}
